/**
 * Professional Legal Document Templates
 * Each template is designed to be comprehensive and legally sound
 */

export const starterTemplates = [
    // ═══════════════════════════════════════════════════════════════════════════
    // SALE CATEGORY
    // ═══════════════════════════════════════════════════════════════════════════
    {
        id: 'sale-agreement-residential',
        title: 'Agreement for Sale of Residential Property',
        category: 'Sale',
        tags: ['Sale', 'Residential', 'Property Transfer'],
        jurisdiction: 'India',
        status: 'published',
        variables: [
            { key: 'agreement_date', label: 'Agreement Date', type: 'date', required: true },
            { key: 'seller_name', label: 'Seller Full Name', type: 'text', required: true },
            { key: 'seller_father_name', label: "Seller's Father/Husband Name", type: 'text', required: true },
            { key: 'seller_age', label: 'Seller Age', type: 'number', required: true },
            { key: 'seller_address', label: 'Seller Address', type: 'text', required: true },
            { key: 'seller_pan', label: 'Seller PAN Number', type: 'text', required: true },
            { key: 'seller_aadhaar', label: 'Seller Aadhaar Number', type: 'text', required: false },
            { key: 'buyer_name', label: 'Buyer Full Name', type: 'text', required: true },
            { key: 'buyer_father_name', label: "Buyer's Father/Husband Name", type: 'text', required: true },
            { key: 'buyer_age', label: 'Buyer Age', type: 'number', required: true },
            { key: 'buyer_address', label: 'Buyer Address', type: 'text', required: true },
            { key: 'buyer_pan', label: 'Buyer PAN Number', type: 'text', required: true },
            { key: 'buyer_aadhaar', label: 'Buyer Aadhaar Number', type: 'text', required: false },
            { key: 'property_address', label: 'Property Address', type: 'text', required: true },
            { key: 'property_area_sqft', label: 'Built-up Area (Sq. Ft.)', type: 'number', required: true },
            { key: 'property_type', label: 'Property Type (Flat/House/Villa)', type: 'text', required: true },
            { key: 'floor_number', label: 'Floor Number', type: 'text', required: false },
            { key: 'survey_number', label: 'Survey/Plot Number', type: 'text', required: true },
            { key: 'municipal_number', label: 'Municipal/House Number', type: 'text', required: true },
            { key: 'total_sale_price', label: 'Total Sale Consideration (₹)', type: 'currency', required: true },
            { key: 'earnest_money', label: 'Earnest Money/Token Amount (₹)', type: 'currency', required: true },
            { key: 'earnest_money_date', label: 'Earnest Money Payment Date', type: 'date', required: true },
            { key: 'balance_amount', label: 'Balance Amount (₹)', type: 'currency', required: true },
            { key: 'completion_date', label: 'Completion/Registry Date', type: 'date', required: true },
            { key: 'possession_date', label: 'Possession Date', type: 'date', required: true },
            { key: 'witness_1_name', label: 'Witness 1 Name', type: 'text', required: true },
            { key: 'witness_1_address', label: 'Witness 1 Address', type: 'text', required: true },
            { key: 'witness_2_name', label: 'Witness 2 Name', type: 'text', required: true },
            { key: 'witness_2_address', label: 'Witness 2 Address', type: 'text', required: true },
        ],
        blocks: [
            {
                id: 'b1',
                type: 'heading',
                content: 'AGREEMENT FOR SALE OF IMMOVABLE PROPERTY',
            },
            {
                id: 'b2',
                type: 'paragraph',
                content: `This Agreement for Sale ("Agreement") is made and executed on this {{agreement_date}} at the below mentioned place.

BETWEEN

{{seller_name}}, aged about {{seller_age}} years, Son/Daughter/Wife of {{seller_father_name}}, residing at {{seller_address}}, holding PAN: {{seller_pan}} and Aadhaar: {{seller_aadhaar}} (hereinafter referred to as the "SELLER/VENDOR", which expression shall, unless repugnant to the context or meaning thereof, include his/her heirs, executors, administrators, legal representatives and assigns) of the FIRST PART;

AND

{{buyer_name}}, aged about {{buyer_age}} years, Son/Daughter/Wife of {{buyer_father_name}}, residing at {{buyer_address}}, holding PAN: {{buyer_pan}} and Aadhaar: {{buyer_aadhaar}} (hereinafter referred to as the "BUYER/PURCHASER", which expression shall, unless repugnant to the context or meaning thereof, include his/her heirs, executors, administrators, legal representatives and assigns) of the SECOND PART.

The Seller and the Buyer are hereinafter collectively referred to as "Parties" and individually as "Party".`,
            },
            {
                id: 'b3',
                type: 'heading',
                content: 'RECITALS',
            },
            {
                id: 'b4',
                type: 'paragraph',
                content: `WHEREAS:

A. The Seller is the sole and absolute owner and is in lawful possession of the residential property bearing {{property_type}} No. {{municipal_number}}, situated at {{property_address}}, admeasuring {{property_area_sqft}} square feet (built-up area), lying in Survey No./Plot No. {{survey_number}} (hereinafter referred to as the "SCHEDULED PROPERTY" or "SAID PROPERTY").

B. The Seller has acquired the said property through [Sale Deed/Gift Deed/Partition/Inheritance] dated _____________ and registered as Document No. _____________ in the office of the Sub-Registrar of _____________, and has been in continuous, peaceful, and uninterrupted possession and enjoyment of the same.

C. The Seller has represented and warranted that the Scheduled Property is free from all encumbrances, charges, liens, mortgages, litigations, attachments, acquisitions, requisitions, agreements, claims, demands, and disputes of any nature whatsoever.

D. The Buyer, after inspecting the property, its title documents, and being satisfied with the same, has agreed to purchase the Scheduled Property from the Seller for the total sale consideration of Rs. {{total_sale_price}}/- (Rupees _________________________ Only).

E. The Seller has agreed to sell, transfer, and convey all his/her rights, title, interest, and ownership in the Scheduled Property to the Buyer on the terms and conditions contained herein.

NOW, THEREFORE, in consideration of the mutual promises and covenants contained herein and for other good and valuable consideration, the receipt and sufficiency of which are hereby acknowledged, the Parties agree as follows:`,
            },
            {
                id: 'b5',
                type: 'heading',
                content: '1. SALE CONSIDERATION AND PAYMENT TERMS',
            },
            {
                id: 'b6',
                type: 'clause',
                content: `1.1 Total Sale Consideration: The total sale consideration for the Scheduled Property has been mutually fixed and agreed at Rs. {{total_sale_price}}/- (Rupees _________________________ Only), which includes the value of the undivided share of land, building, fixtures, fittings, and all appurtenances thereto.

1.2 Earnest Money: The Buyer has paid a sum of Rs. {{earnest_money}}/- (Rupees _________________________ Only) as earnest money/token advance on {{earnest_money_date}} to the Seller by way of [Cheque No./RTGS/NEFT/UPI Ref. No.] _____________, the receipt of which is hereby acknowledged by the Seller.

1.3 Balance Amount: The balance amount of Rs. {{balance_amount}}/- (Rupees _________________________ Only) shall be paid by the Buyer to the Seller at the time of execution and registration of the Sale Deed on or before {{completion_date}}.

1.4 Mode of Payment: All payments shall be made through banking channels (RTGS/NEFT/Demand Draft/Cheque) only. Cash transactions exceeding Rs. 2,00,000/- are prohibited under Section 269ST of the Income Tax Act, 1961.

1.5 Payment Schedule:
(a) Token/Earnest Money: Rs. {{earnest_money}}/- - PAID
(b) Balance on Registration: Rs. {{balance_amount}}/- - On or before {{completion_date}}`,
            },
            {
                id: 'b7',
                type: 'heading',
                content: '2. SELLER\'S REPRESENTATIONS AND WARRANTIES',
            },
            {
                id: 'b8',
                type: 'clause',
                content: `The Seller hereby represents, warrants, and covenants to the Buyer as follows:

2.1 The Seller is the sole, absolute, and lawful owner of the Scheduled Property and has clear, marketable, and unencumbered title to the same.

2.2 The Seller has full power, right, and authority to enter into this Agreement and to sell, transfer, and convey the Scheduled Property to the Buyer.

2.3 The Scheduled Property is free from all encumbrances, charges, liens, mortgages, easements, rights of way, trusts, prior agreements to sell, court attachments, acquisition proceedings, tax liens, and all other claims of whatsoever nature.

2.4 There are no pending litigations, disputes, claims, or proceedings (civil, criminal, revenue, or others) affecting the Scheduled Property or the Seller's right to sell the same.

2.5 The Seller has not entered into any agreement for sale, lease, license, development, joint venture, or any other arrangement with any third party with respect to the Scheduled Property.

2.6 All taxes, cess, duties, charges, maintenance fees, electricity bills, water bills, society charges, and other outgoings in respect of the Scheduled Property have been paid up to date.

2.7 The Scheduled Property has been constructed in accordance with the sanctioned building plan and has obtained the Occupancy Certificate/Completion Certificate from the competent authority.

2.8 The Seller shall indemnify and keep indemnified the Buyer against all losses, claims, damages, costs, and expenses arising from any breach of the above representations and warranties.`,
            },
            {
                id: 'b9',
                type: 'heading',
                content: '3. BUYER\'S OBLIGATIONS',
            },
            {
                id: 'b10',
                type: 'clause',
                content: `3.1 The Buyer shall pay the balance sale consideration within the stipulated time as mentioned in Clause 1.

3.2 The Buyer shall bear and pay the stamp duty, registration charges, and other incidental expenses for registration of the Sale Deed.

3.3 The Buyer shall cooperate with the Seller in completing the sale transaction and shall provide all necessary documents and information as required.

3.4 The Buyer shall, after the execution of the Sale Deed, be responsible for the transfer of electricity connection, water connection, gas connection, and mutation of the property in his/her name at his/her own costs.`,
            },
            {
                id: 'b11',
                type: 'heading',
                content: '4. COMPLETION OF SALE',
            },
            {
                id: 'b12',
                type: 'clause',
                content: `4.1 Execution of Sale Deed: The Seller agrees to execute the registered Sale Deed in favor of the Buyer or his/her nominee(s) on or before {{completion_date}}, upon receipt of the full sale consideration.

4.2 Documents to be Handed Over: At the time of registration, the Seller shall hand over to the Buyer all original title documents, previous Sale Deeds, sanctioned plan, Occupancy Certificate, tax receipts, society share certificate (if applicable), and all other documents pertaining to the Scheduled Property.

4.3 Possession: Physical, vacant, and peaceful possession of the Scheduled Property shall be handed over by the Seller to the Buyer on {{possession_date}}, along with all keys.`,
            },
            {
                id: 'b13',
                type: 'heading',
                content: '5. DEFAULT AND REMEDIES',
            },
            {
                id: 'b14',
                type: 'clause',
                content: `5.1 Default by Buyer: If the Buyer fails to pay the balance consideration and complete the sale within the stipulated time, the Seller shall be entitled to:
(a) Forfeit the earnest money paid by the Buyer; OR
(b) Seek specific performance of this Agreement with damages.

5.2 Default by Seller: If the Seller fails or refuses to execute and register the Sale Deed within the stipulated time, or commits a breach of any warranty, the Buyer shall be entitled to:
(a) Recover double the earnest money from the Seller; OR
(b) Seek specific performance of this Agreement with damages.

5.3 Time is of Essence: Time is of the essence of this Agreement with respect to all obligations of both Parties.`,
            },
            {
                id: 'b15',
                type: 'heading',
                content: '6. GENERAL PROVISIONS',
            },
            {
                id: 'b16',
                type: 'clause',
                content: `6.1 Governing Law: This Agreement shall be governed by and construed in accordance with the laws of India, including but not limited to the Transfer of Property Act, 1882, the Registration Act, 1908, the Indian Stamp Act, 1899, and the Indian Contract Act, 1872.

6.2 Jurisdiction: Any disputes arising out of or in connection with this Agreement shall be subject to the exclusive jurisdiction of the courts at [City].

6.3 Entire Agreement: This Agreement constitutes the entire understanding between the Parties and supersedes all prior negotiations, representations, and agreements between them.

6.4 Amendments: No modification or amendment of this Agreement shall be valid unless made in writing and signed by both Parties.

6.5 Severability: If any provision of this Agreement is held to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.

6.6 Notices: All notices under this Agreement shall be in writing and shall be deemed to have been duly given when delivered by hand or sent by registered post/speed post/courier to the addresses mentioned above.`,
            },
            {
                id: 'b17',
                type: 'heading',
                content: 'SCHEDULE OF PROPERTY',
            },
            {
                id: 'b18',
                type: 'paragraph',
                content: `All that piece and parcel of the {{property_type}} bearing No. {{municipal_number}}, situated on Floor {{floor_number}}, constructed on Survey No./Plot No. {{survey_number}}, at {{property_address}}, admeasuring {{property_area_sqft}} square feet (built-up/super built-up area), bounded as follows:

On the East by: _______________________
On the West by: _______________________
On the North by: _______________________
On the South by: _______________________

Together with the undivided proportionate share in the common areas, parking space (if any), and all rights, easements, and appurtenances belonging thereto.`,
            },
            {
                id: 'b19',
                type: 'signature',
                content: `IN WITNESS WHEREOF, the Parties have hereunto set their hands and signatures on the day, month, and year first above written.


________________________                    ________________________
SELLER                                      BUYER
{{seller_name}}                             {{buyer_name}}


WITNESSES:

1. Signature: ___________________
   Name: {{witness_1_name}}
   Address: {{witness_1_address}}

2. Signature: ___________________
   Name: {{witness_2_name}}
   Address: {{witness_2_address}}


Place: _______________________
Date: {{agreement_date}}`,
            },
        ],
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // RENTAL CATEGORY
    // ═══════════════════════════════════════════════════════════════════════════
    {
        id: 'rental-agreement-residential',
        title: 'Residential Rental/Lease Agreement',
        category: 'Rental',
        tags: ['Rent', 'Lease', 'Residential', 'Tenant'],
        jurisdiction: 'India',
        status: 'published',
        variables: [
            { key: 'agreement_date', label: 'Agreement Date', type: 'date', required: true },
            { key: 'landlord_name', label: 'Landlord Full Name', type: 'text', required: true },
            { key: 'landlord_father_name', label: "Landlord's Father Name", type: 'text', required: true },
            { key: 'landlord_age', label: 'Landlord Age', type: 'number', required: true },
            { key: 'landlord_address', label: 'Landlord Permanent Address', type: 'text', required: true },
            { key: 'landlord_pan', label: 'Landlord PAN', type: 'text', required: true },
            { key: 'tenant_name', label: 'Tenant Full Name', type: 'text', required: true },
            { key: 'tenant_father_name', label: "Tenant's Father Name", type: 'text', required: true },
            { key: 'tenant_age', label: 'Tenant Age', type: 'number', required: true },
            { key: 'tenant_permanent_address', label: 'Tenant Permanent Address', type: 'text', required: true },
            { key: 'tenant_pan', label: 'Tenant PAN', type: 'text', required: true },
            { key: 'tenant_aadhaar', label: 'Tenant Aadhaar', type: 'text', required: true },
            { key: 'property_address', label: 'Rental Property Address', type: 'text', required: true },
            { key: 'property_type', label: 'Property Type (1BHK/2BHK/etc.)', type: 'text', required: true },
            { key: 'carpet_area', label: 'Carpet Area (Sq. Ft.)', type: 'number', required: true },
            { key: 'monthly_rent', label: 'Monthly Rent (₹)', type: 'currency', required: true },
            { key: 'security_deposit', label: 'Security Deposit (₹)', type: 'currency', required: true },
            { key: 'maintenance_charges', label: 'Monthly Maintenance (₹)', type: 'currency', required: false },
            { key: 'lease_start_date', label: 'Lease Start Date', type: 'date', required: true },
            { key: 'lease_end_date', label: 'Lease End Date', type: 'date', required: true },
            { key: 'lease_duration_months', label: 'Lease Duration (Months)', type: 'number', required: true },
            { key: 'rent_due_date', label: 'Rent Due Date (Day of Month)', type: 'number', required: true },
            { key: 'lock_in_months', label: 'Lock-in Period (Months)', type: 'number', required: true },
            { key: 'notice_period_months', label: 'Notice Period (Months)', type: 'number', required: true },
            { key: 'annual_increment_percent', label: 'Annual Rent Increment (%)', type: 'number', required: false },
            { key: 'witness_1_name', label: 'Witness 1 Name', type: 'text', required: true },
            { key: 'witness_2_name', label: 'Witness 2 Name', type: 'text', required: true },
        ],
        blocks: [
            {
                id: 'b1',
                type: 'heading',
                content: 'RESIDENTIAL RENTAL/LEASE AGREEMENT',
            },
            {
                id: 'b2',
                type: 'paragraph',
                content: `This Rental/Lease Agreement ("Agreement") is made and executed on this {{agreement_date}}.

BETWEEN

{{landlord_name}}, aged about {{landlord_age}} years, Son/Daughter of {{landlord_father_name}}, residing at {{landlord_address}}, PAN: {{landlord_pan}} (hereinafter called the "LANDLORD/LESSOR", which expression shall include his/her heirs, executors, administrators, and assigns) of the FIRST PART;

AND

{{tenant_name}}, aged about {{tenant_age}} years, Son/Daughter of {{tenant_father_name}}, permanently residing at {{tenant_permanent_address}}, PAN: {{tenant_pan}}, Aadhaar: {{tenant_aadhaar}} (hereinafter called the "TENANT/LESSEE", which expression shall include his/her heirs, executors, administrators, and assigns) of the SECOND PART.`,
            },
            {
                id: 'b3',
                type: 'heading',
                content: 'RECITALS',
            },
            {
                id: 'b4',
                type: 'paragraph',
                content: `WHEREAS the Landlord is the owner of the residential property described hereinbelow and is desirous of letting out the same to the Tenant for residential purposes only.

AND WHEREAS the Tenant has approached the Landlord for taking the said premises on rent/lease for residential use and the Landlord has agreed to let out the same on the terms and conditions hereinafter contained.

NOW THIS AGREEMENT WITNESSETH as follows:`,
            },
            {
                id: 'b5',
                type: 'heading',
                content: '1. DESCRIPTION OF PREMISES',
            },
            {
                id: 'b6',
                type: 'clause',
                content: `1.1 The Landlord hereby lets and the Tenant hereby takes on rent/lease the following premises:

Property Type: {{property_type}}
Address: {{property_address}}
Carpet Area: {{carpet_area}} square feet

Including: The demised premises shall include all fixtures, fittings, sanitary fittings, electrical fittings, fans, geysers, kitchen cabinets (if any), and other amenities as listed in the inventory annexed hereto.`,
            },
            {
                id: 'b7',
                type: 'heading',
                content: '2. LEASE TERM',
            },
            {
                id: 'b8',
                type: 'clause',
                content: `2.1 The lease shall commence from {{lease_start_date}} and shall continue for a period of {{lease_duration_months}} months, expiring on {{lease_end_date}}.

2.2 Lock-in Period: Both parties agree to a lock-in period of {{lock_in_months}} months from the commencement date, during which neither party shall terminate this Agreement.

2.3 Renewal: Upon expiry of the lease term, the same may be renewed for a further period by mutual consent and upon such revised terms as may be agreed between the parties.

2.4 Notice for Termination: Either party wishing to terminate this Agreement after the lock-in period shall give {{notice_period_months}} months' prior written notice to the other party.`,
            },
            {
                id: 'b9',
                type: 'heading',
                content: '3. RENT AND OTHER PAYMENTS',
            },
            {
                id: 'b10',
                type: 'clause',
                content: `3.1 Monthly Rent: The Tenant agrees to pay a monthly rent of Rs. {{monthly_rent}}/- (Rupees _________________________ Only), payable on or before the {{rent_due_date}}th day of each calendar month.

3.2 Security Deposit: The Tenant has paid a sum of Rs. {{security_deposit}}/- (Rupees _________________________ Only) as interest-free refundable security deposit to the Landlord, the receipt of which is hereby acknowledged. This deposit shall be refunded to the Tenant at the time of vacating the premises, after deducting any outstanding dues, damages, or costs of repairs (beyond normal wear and tear).

3.3 Maintenance Charges: In addition to the rent, the Tenant shall pay monthly maintenance charges of Rs. {{maintenance_charges}}/- to the society/association, as applicable.

3.4 Utilities: The Tenant shall pay directly for electricity, water, gas, telephone, internet, cable TV, and any other utility charges as per actual consumption/bills.

3.5 Annual Increment: The monthly rent shall be increased by {{annual_increment_percent}}% at the end of each year of the tenancy, or as mutually agreed.

3.6 Late Payment: If rent is not paid within 7 days of the due date, the Tenant shall pay an additional late fee of Rs. 100/- per day of delay.`,
            },
            {
                id: 'b11',
                type: 'heading',
                content: '4. TENANT\'S COVENANTS AND OBLIGATIONS',
            },
            {
                id: 'b12',
                type: 'clause',
                content: `The Tenant hereby covenants and agrees:

4.1 To use the premises solely for residential purposes and not for any commercial, illegal, or immoral activities.

4.2 To keep the premises in good and tenantable condition and to hand over the same in the same condition (subject to normal wear and tear) at the end of the tenancy.

4.3 Not to make any structural alterations, additions, or modifications to the premises without prior written consent of the Landlord.

4.4 Not to sub-let, assign, or part with possession of the whole or any portion of the premises to any person whatsoever.

4.5 To permit the Landlord or his authorized representative to enter the premises for inspection at reasonable hours upon prior notice.

4.6 Not to cause any nuisance or annoyance to other occupants of the building or neighbors.

4.7 To inform the Landlord immediately of any damage or defect in the premises.

4.8 To comply with all rules and regulations of the housing society/apartment association.

4.9 To complete police verification and other formalities as required by law.`,
            },
            {
                id: 'b13',
                type: 'heading',
                content: '5. LANDLORD\'S COVENANTS AND OBLIGATIONS',
            },
            {
                id: 'b14',
                type: 'clause',
                content: `The Landlord hereby covenants and agrees:

5.1 To deliver peaceful and vacant possession of the premises to the Tenant on the commencement date.

5.2 To ensure that the premises are in good and habitable condition at the time of handing over.

5.3 To pay property tax, society maintenance charges attributable to the owner, and any capital expenses for major repairs or structural works.

5.4 To carry out major repairs relating to structural defects, seepage, leakage, etc., at his own cost.

5.5 Not to interfere with the peaceful enjoyment of the premises by the Tenant so long as the Tenant complies with the terms of this Agreement.`,
            },
            {
                id: 'b15',
                type: 'heading',
                content: '6. TERMINATION',
            },
            {
                id: 'b16',
                type: 'clause',
                content: `6.1 This Agreement may be terminated:
(a) By mutual written consent of both parties;
(b) By either party giving {{notice_period_months}} months' prior written notice after the lock-in period;
(c) By the Landlord for breach of any material term by the Tenant;
(d) By the Tenant if the premises become uninhabitable due to reasons not attributable to the Tenant.

6.2 Upon termination, the Tenant shall:
(a) Clear all outstanding dues;
(b) Hand over vacant possession of the premises;
(c) Return all keys and access cards;
(d) Leave behind all fixtures and fittings provided by the Landlord.

6.3 The Landlord shall refund the security deposit within 30 days of vacating, after adjustment of dues and damages.`,
            },
            {
                id: 'b17',
                type: 'heading',
                content: '7. GENERAL PROVISIONS',
            },
            {
                id: 'b18',
                type: 'clause',
                content: `7.1 This Agreement shall be governed by the laws of India and the applicable Rent Control Act.

7.2 Any dispute arising under this Agreement shall be subject to the jurisdiction of the courts at [City].

7.3 This Agreement may be registered under the Registration Act, 1908, at the cost of the Tenant.

7.4 This Agreement constitutes the entire understanding between the parties.`,
            },
            {
                id: 'b19',
                type: 'heading',
                content: 'SCHEDULE - INVENTORY OF FIXTURES AND FITTINGS',
            },
            {
                id: 'b20',
                type: 'paragraph',
                content: `[To be filled and signed by both parties]

1. Electrical Fittings: _______________
2. Fans/ACs: _______________
3. Geysers: _______________
4. Kitchen Fixtures: _______________
5. Bathroom Fittings: _______________
6. Wardrobes/Cabinets: _______________
7. Others: _______________`,
            },
            {
                id: 'b21',
                type: 'signature',
                content: `IN WITNESS WHEREOF, the parties have executed this Agreement on the date first above written.


________________________                    ________________________
LANDLORD                                    TENANT
{{landlord_name}}                           {{tenant_name}}


WITNESSES:

1. Name: {{witness_1_name}}
   Signature: ___________________

2. Name: {{witness_2_name}}
   Signature: ___________________


Place: _______________________
Date: {{agreement_date}}`,
            },
        ],
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // BROKERAGE CATEGORY
    // ═══════════════════════════════════════════════════════════════════════════
    {
        id: 'brokerage-agreement-exclusive',
        title: 'Exclusive Property Brokerage Agreement',
        category: 'Brokerage',
        tags: ['Brokerage', 'Commission', 'Exclusive', 'Agent'],
        jurisdiction: 'India',
        status: 'published',
        variables: [
            { key: 'agreement_date', label: 'Agreement Date', type: 'date', required: true },
            { key: 'broker_name', label: 'Broker/Agent Name', type: 'text', required: true },
            { key: 'broker_company', label: 'Brokerage Firm Name', type: 'text', required: true },
            { key: 'broker_rera_number', label: 'Broker RERA Registration No.', type: 'text', required: true },
            { key: 'broker_address', label: 'Broker Office Address', type: 'text', required: true },
            { key: 'broker_phone', label: 'Broker Phone', type: 'text', required: true },
            { key: 'broker_email', label: 'Broker Email', type: 'text', required: true },
            { key: 'owner_name', label: 'Property Owner Name', type: 'text', required: true },
            { key: 'owner_address', label: 'Owner Address', type: 'text', required: true },
            { key: 'owner_phone', label: 'Owner Phone', type: 'text', required: true },
            { key: 'property_address', label: 'Property Address', type: 'text', required: true },
            { key: 'property_type', label: 'Property Type', type: 'text', required: true },
            { key: 'property_area', label: 'Property Area (Sq. Ft.)', type: 'number', required: true },
            { key: 'listing_price', label: 'Listing Price (₹)', type: 'currency', required: true },
            { key: 'transaction_type', label: 'Transaction Type (Sale/Rent)', type: 'text', required: true },
            { key: 'commission_percent', label: 'Commission Percentage (%)', type: 'number', required: true },
            { key: 'commission_amount', label: 'Fixed Commission Amount (₹)', type: 'currency', required: false },
            { key: 'agreement_validity_months', label: 'Agreement Validity (Months)', type: 'number', required: true },
            { key: 'exclusivity_period_months', label: 'Exclusivity Period (Months)', type: 'number', required: true },
        ],
        blocks: [
            {
                id: 'b1',
                type: 'heading',
                content: 'EXCLUSIVE PROPERTY BROKERAGE AGREEMENT',
            },
            {
                id: 'b2',
                type: 'paragraph',
                content: `This Exclusive Brokerage Agreement ("Agreement") is entered into on {{agreement_date}}.

BETWEEN

{{owner_name}}, residing at {{owner_address}}, Contact: {{owner_phone}} (hereinafter referred to as the "OWNER/PRINCIPAL") of the FIRST PART;

AND

{{broker_company}}, represented by {{broker_name}}, having its office at {{broker_address}}, RERA Registration No.: {{broker_rera_number}}, Contact: {{broker_phone}}, Email: {{broker_email}} (hereinafter referred to as the "BROKER/AGENT") of the SECOND PART.`,
            },
            {
                id: 'b3',
                type: 'heading',
                content: 'RECITALS',
            },
            {
                id: 'b4',
                type: 'paragraph',
                content: `WHEREAS the Owner is the lawful owner of the property described herein and desires to engage the services of a real estate broker to [sell/lease] the property.

AND WHEREAS the Broker is a registered and licensed real estate agent under the Real Estate (Regulation and Development) Act, 2016, and possesses the expertise and resources to market and facilitate real estate transactions.

NOW THEREFORE, in consideration of the mutual promises contained herein, the parties agree as follows:`,
            },
            {
                id: 'b5',
                type: 'heading',
                content: '1. APPOINTMENT AND SCOPE',
            },
            {
                id: 'b6',
                type: 'clause',
                content: `1.1 The Owner hereby appoints the Broker as the EXCLUSIVE agent for the {{transaction_type}} of the following property:

Property Type: {{property_type}}
Address: {{property_address}}
Area: {{property_area}} square feet
Listing Price: Rs. {{listing_price}}/-

1.2 "Exclusive" means that during the term of this Agreement:
(a) The Broker shall be the sole agent authorized to market the property;
(b) The Owner shall not engage any other broker or agent for the same property;
(c) The Owner shall refer all inquiries to the Broker;
(d) Commission shall be payable to the Broker even if the transaction is concluded directly by the Owner with a buyer/tenant introduced by any source during the exclusivity period.`,
            },
            {
                id: 'b7',
                type: 'heading',
                content: '2. TERM AND VALIDITY',
            },
            {
                id: 'b8',
                type: 'clause',
                content: `2.1 This Agreement shall be valid for a period of {{agreement_validity_months}} months from the date of execution.

2.2 The exclusivity period shall be {{exclusivity_period_months}} months from the commencement date.

2.3 After the exclusivity period, the Owner may engage other brokers, but this Agreement shall continue on a non-exclusive basis until expiry.

2.4 This Agreement may be extended by mutual written consent.`,
            },
            {
                id: 'b9',
                type: 'heading',
                content: '3. BROKER\'S DUTIES AND OBLIGATIONS',
            },
            {
                id: 'b10',
                type: 'clause',
                content: `The Broker agrees to:

3.1 Market the property through appropriate channels including online portals, print media, signage, and direct marketing.

3.2 Conduct site visits with prospective buyers/tenants after scheduling with the Owner.

3.3 Verify the credentials and genuineness of prospective buyers/tenants.

3.4 Negotiate on behalf of the Owner to secure the best possible terms.

3.5 Assist in documentation, due diligence, and coordination with lawyers and authorities.

3.6 Maintain confidentiality of the Owner's personal and financial information.

3.7 Comply with all RERA regulations and code of conduct for real estate agents.

3.8 Provide regular updates to the Owner on marketing activities and inquiries received.`,
            },
            {
                id: 'b11',
                type: 'heading',
                content: '4. OWNER\'S DUTIES AND OBLIGATIONS',
            },
            {
                id: 'b12',
                type: 'clause',
                content: `The Owner agrees to:

4.1 Provide accurate and complete information about the property, including title documents, encumbrances, and defects.

4.2 Make the property available for inspection at reasonable times.

4.3 Not engage any other broker during the exclusivity period.

4.4 Refer all direct inquiries to the Broker.

4.5 Pay the agreed commission upon successful completion of the transaction.

4.6 Provide necessary documents and cooperation for completing the transaction.`,
            },
            {
                id: 'b13',
                type: 'heading',
                content: '5. COMMISSION AND PAYMENT',
            },
            {
                id: 'b14',
                type: 'clause',
                content: `5.1 Commission Rate: The Owner agrees to pay the Broker a commission of {{commission_percent}}% of the transaction value, or a fixed sum of Rs. {{commission_amount}}/-, whichever is higher.

5.2 Payment Trigger: Commission shall become due and payable upon:
(a) For Sale: Execution of the registered Sale Deed and receipt of full consideration by the Owner;
(b) For Rent: Signing of the Rent Agreement and receipt of security deposit and first month's rent.

5.3 Payment Timeline: Commission shall be paid within 7 days of the triggering event.

5.4 GST: The commission amount is exclusive of GST, which shall be borne by the Owner.

5.5 Protected Period: If within 6 months after expiry/termination of this Agreement, a transaction is concluded with any party introduced by the Broker during the term, the full commission shall be payable.`,
            },
            {
                id: 'b15',
                type: 'heading',
                content: '6. TERMINATION',
            },
            {
                id: 'b16',
                type: 'clause',
                content: `6.1 This Agreement may be terminated:
(a) By mutual written consent;
(b) By either party with 15 days' written notice after the exclusivity period;
(c) Immediately, for material breach by the other party.

6.2 Upon termination, the Broker shall:
(a) Cease all marketing activities;
(b) Return all documents and keys to the Owner;
(c) Provide a list of all parties shown/introduced.

6.3 Termination shall not affect accrued rights, including commission rights for transactions with introduced parties.`,
            },
            {
                id: 'b17',
                type: 'signature',
                content: `IN WITNESS WHEREOF, the parties have executed this Agreement on the date first above written.


________________________                    ________________________
OWNER                                       BROKER
{{owner_name}}                              {{broker_name}}
                                            For {{broker_company}}
                                            RERA No.: {{broker_rera_number}}

Date: {{agreement_date}}
Place: _______________________`,
            },
        ],
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // CONSTRUCTION CATEGORY
    // ═══════════════════════════════════════════════════════════════════════════
    {
        id: 'construction-work-order',
        title: 'Construction Work Order',
        category: 'Construction',
        tags: ['Work Order', 'Construction', 'Contractor'],
        jurisdiction: 'India',
        status: 'published',
        variables: [
            { key: 'work_order_number', label: 'Work Order Number', type: 'text', required: true },
            { key: 'work_order_date', label: 'Work Order Date', type: 'date', required: true },
            { key: 'owner_name', label: 'Owner/Client Name', type: 'text', required: true },
            { key: 'owner_company', label: 'Owner Company (if applicable)', type: 'text', required: false },
            { key: 'owner_address', label: 'Owner Address', type: 'text', required: true },
            { key: 'owner_phone', label: 'Owner Phone', type: 'text', required: true },
            { key: 'contractor_name', label: 'Contractor Name', type: 'text', required: true },
            { key: 'contractor_company', label: 'Contractor Company', type: 'text', required: true },
            { key: 'contractor_gstin', label: 'Contractor GSTIN', type: 'text', required: true },
            { key: 'contractor_address', label: 'Contractor Address', type: 'text', required: true },
            { key: 'contractor_phone', label: 'Contractor Phone', type: 'text', required: true },
            { key: 'project_name', label: 'Project Name', type: 'text', required: true },
            { key: 'site_address', label: 'Site/Project Address', type: 'text', required: true },
            { key: 'work_description', label: 'Description of Work', type: 'text', required: true },
            { key: 'total_contract_value', label: 'Total Contract Value (₹)', type: 'currency', required: true },
            { key: 'advance_payment', label: 'Advance Payment (₹)', type: 'currency', required: true },
            { key: 'start_date', label: 'Work Start Date', type: 'date', required: true },
            { key: 'completion_date', label: 'Expected Completion Date', type: 'date', required: true },
            { key: 'defect_liability_months', label: 'Defect Liability Period (Months)', type: 'number', required: true },
            { key: 'penalty_per_day', label: 'Delay Penalty per Day (₹)', type: 'currency', required: true },
        ],
        blocks: [
            {
                id: 'b1',
                type: 'heading',
                content: 'WORK ORDER FOR CONSTRUCTION SERVICES',
            },
            {
                id: 'b2',
                type: 'paragraph',
                content: `WORK ORDER NO.: {{work_order_number}}
DATE: {{work_order_date}}

FROM (OWNER/CLIENT):
{{owner_name}}
{{owner_company}}
{{owner_address}}
Phone: {{owner_phone}}

TO (CONTRACTOR):
{{contractor_company}}
Represented by: {{contractor_name}}
GSTIN: {{contractor_gstin}}
{{contractor_address}}
Phone: {{contractor_phone}}`,
            },
            {
                id: 'b3',
                type: 'paragraph',
                content: `Dear Sir/Madam,

With reference to your quotation dated _____________ and subsequent negotiations, you are hereby awarded the following work on the terms and conditions specified herein:`,
            },
            {
                id: 'b4',
                type: 'heading',
                content: '1. SCOPE OF WORK',
            },
            {
                id: 'b5',
                type: 'clause',
                content: `1.1 Project Name: {{project_name}}

1.2 Site Address: {{site_address}}

1.3 Description of Work:
{{work_description}}

1.4 The scope includes:
(a) All labor, materials, tools, equipment, and supervision required for the work;
(b) Compliance with approved drawings, specifications, and BOQ;
(c) Safety measures and compliance with all statutory requirements;
(d) Testing and commissioning;
(e) Cleanup and removal of debris.

1.5 Any additional work not covered in this Work Order shall be executed only upon written approval and separate work order.`,
            },
            {
                id: 'b6',
                type: 'heading',
                content: '2. CONTRACT VALUE AND PAYMENT TERMS',
            },
            {
                id: 'b7',
                type: 'clause',
                content: `2.1 Total Contract Value: Rs. {{total_contract_value}}/- (Rupees _________________________ Only), inclusive of all taxes except GST.

2.2 GST: As applicable, shall be paid extra against valid tax invoice.

2.3 Payment Schedule:
(a) Advance Payment: Rs. {{advance_payment}}/- upon signing of this Work Order;
(b) Running Bills: As per progress, measured and certified by the Owner's Engineer, payable within 15 days;
(c) Final Payment: Within 30 days of completion and final handover.

2.4 Retention: 5% of each running bill shall be retained as security, to be released after the defect liability period.

2.5 TDS: Tax Deduction at Source shall be deducted as per applicable rates under the Income Tax Act.`,
            },
            {
                id: 'b8',
                type: 'heading',
                content: '3. TIME SCHEDULE',
            },
            {
                id: 'b9',
                type: 'clause',
                content: `3.1 Commencement Date: The work shall commence on or before {{start_date}}.

3.2 Completion Date: The entire work shall be completed on or before {{completion_date}}.

3.3 Time is of the Essence: Time is of the essence of this contract. The Contractor shall adhere strictly to the agreed schedule.

3.4 Extension of Time: Extension may be granted only for reasons beyond the Contractor's control (Force Majeure, Owner-caused delays), upon written request with supporting documents.`,
            },
            {
                id: 'b10',
                type: 'heading',
                content: '4. LIQUIDATED DAMAGES FOR DELAY',
            },
            {
                id: 'b11',
                type: 'clause',
                content: `4.1 In case of delay in completion beyond the stipulated date (without approved extension), the Contractor shall pay liquidated damages at the rate of Rs. {{penalty_per_day}}/- per day of delay.

4.2 The maximum liquidated damages shall not exceed 10% of the total contract value.

4.3 Such damages shall be recoverable from any dues payable to the Contractor.`,
            },
            {
                id: 'b12',
                type: 'heading',
                content: '5. DEFECT LIABILITY PERIOD',
            },
            {
                id: 'b13',
                type: 'clause',
                content: `5.1 The Contractor shall be liable for rectifying any defects arising from faulty materials or workmanship for a period of {{defect_liability_months}} months from the date of completion/handover.

5.2 During this period, the Contractor shall rectify defects at no additional cost within 7 days of notification.

5.3 If the Contractor fails to rectify defects, the Owner may get the work done through others and recover the cost from the retention money.`,
            },
            {
                id: 'b14',
                type: 'heading',
                content: '6. CONTRACTOR\'S OBLIGATIONS',
            },
            {
                id: 'b15',
                type: 'clause',
                content: `The Contractor shall:

6.1 Execute the work in a professional manner, conforming to best industry practices and standards.

6.2 Use only materials of approved quality and specifications.

6.3 Comply with all applicable laws, including labor laws, building codes, and environmental regulations.

6.4 Maintain adequate insurance coverage for workers, third-party liability, and works.

6.5 Ensure safety of workers and third parties at the site.

6.6 Keep the site clean and dispose of waste properly.

6.7 Not assign or subcontract the work without prior written approval.`,
            },
            {
                id: 'b16',
                type: 'heading',
                content: '7. TERMINATION',
            },
            {
                id: 'b17',
                type: 'clause',
                content: `7.1 The Owner may terminate this Work Order:
(a) For persistent default or poor workmanship by the Contractor;
(b) For abandonment of work by the Contractor;
(c) For insolvency or bankruptcy of the Contractor;
(d) For convenience, upon 15 days' notice with payment for work done.

7.2 Upon termination, the Contractor shall:
(a) Stop all work immediately;
(b) Remove all materials and equipment from site;
(c) Hand over completed work and site to the Owner.`,
            },
            {
                id: 'b18',
                type: 'signature',
                content: `Please acknowledge acceptance of this Work Order by signing and returning a copy.

FOR OWNER:                                  FOR CONTRACTOR:

________________________                    ________________________
Authorized Signatory                        Authorized Signatory
{{owner_name}}                              {{contractor_name}}
                                            For {{contractor_company}}

Date: {{work_order_date}}
Place: _______________________`,
            },
        ],
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // COMPLIANCE CATEGORY
    // ═══════════════════════════════════════════════════════════════════════════
    {
        id: 'poa-general',
        title: 'General Power of Attorney',
        category: 'Compliance',
        tags: ['POA', 'Power of Attorney', 'Authorization'],
        jurisdiction: 'India',
        status: 'published',
        variables: [
            { key: 'execution_date', label: 'Execution Date', type: 'date', required: true },
            { key: 'principal_name', label: 'Principal (Grantor) Name', type: 'text', required: true },
            { key: 'principal_father_name', label: "Principal's Father Name", type: 'text', required: true },
            { key: 'principal_age', label: 'Principal Age', type: 'number', required: true },
            { key: 'principal_address', label: 'Principal Address', type: 'text', required: true },
            { key: 'principal_pan', label: 'Principal PAN', type: 'text', required: true },
            { key: 'principal_aadhaar', label: 'Principal Aadhaar', type: 'text', required: true },
            { key: 'agent_name', label: 'Agent (Attorney) Name', type: 'text', required: true },
            { key: 'agent_father_name', label: "Agent's Father Name", type: 'text', required: true },
            { key: 'agent_age', label: 'Agent Age', type: 'number', required: true },
            { key: 'agent_address', label: 'Agent Address', type: 'text', required: true },
            { key: 'agent_pan', label: 'Agent PAN', type: 'text', required: true },
            { key: 'agent_aadhaar', label: 'Agent Aadhaar', type: 'text', required: true },
            { key: 'property_description', label: 'Property Description (if applicable)', type: 'text', required: false },
            { key: 'validity_period', label: 'Validity Period (Years)', type: 'number', required: true },
            { key: 'witness_1_name', label: 'Witness 1 Name', type: 'text', required: true },
            { key: 'witness_2_name', label: 'Witness 2 Name', type: 'text', required: true },
        ],
        blocks: [
            {
                id: 'b1',
                type: 'heading',
                content: 'GENERAL POWER OF ATTORNEY',
            },
            {
                id: 'b2',
                type: 'paragraph',
                content: `KNOW ALL MEN BY THESE PRESENTS

This General Power of Attorney is made and executed on this {{execution_date}}.

BY

{{principal_name}}, aged about {{principal_age}} years, Son/Daughter of {{principal_father_name}}, residing at {{principal_address}}, PAN: {{principal_pan}}, Aadhaar: {{principal_aadhaar}} (hereinafter called the "PRINCIPAL" or "EXECUTANT")

IN FAVOUR OF

{{agent_name}}, aged about {{agent_age}} years, Son/Daughter of {{agent_father_name}}, residing at {{agent_address}}, PAN: {{agent_pan}}, Aadhaar: {{agent_aadhaar}} (hereinafter called the "ATTORNEY" or "AGENT")`,
            },
            {
                id: 'b3',
                type: 'paragraph',
                content: `WHEREAS the Principal is the owner of certain properties, assets, and rights and is desirous of appointing an attorney to manage, administer, and deal with the same on his/her behalf;

AND WHEREAS the Principal has full trust and confidence in the Attorney and desires to authorize the Attorney to act on his/her behalf;

NOW THIS POWER OF ATTORNEY WITNESSETH as follows:`,
            },
            {
                id: 'b4',
                type: 'heading',
                content: 'GRANT OF POWERS',
            },
            {
                id: 'b5',
                type: 'clause',
                content: `The Principal hereby nominates, constitutes, and appoints the Attorney as the true and lawful attorney of the Principal to do, execute, and perform all or any of the following acts, deeds, and things on behalf of the Principal:

1. PROPERTY MATTERS:
(a) To manage, administer, and look after all immovable properties;
(b) To collect rent, issue receipts, and take appropriate action against defaulting tenants;
(c) To let out, lease, license, or rent any property on such terms as deemed fit;
(d) To negotiate, enter into agreements for sale, lease, or mortgage;
(e) To execute Sale Deeds, Conveyance Deeds, and other documents;
(f) To receive and grant possession of properties;
(g) To apply for and obtain building permissions, approvals, and clearances;
(h) To file and pursue cases relating to properties.

2. BANKING AND FINANCIAL MATTERS:
(a) To operate bank accounts, issue cheques, and make payments;
(b) To open, close, or operate locker facilities;
(c) To avail loans, overdrafts, and credit facilities;
(d) To receive payments, dividends, and interest;
(e) To invest, reinvest, or withdraw investments.

3. LEGAL MATTERS:
(a) To file, defend, or withdraw any legal proceedings;
(b) To engage advocates and legal consultants;
(c) To sign, verify, and file plaints, written statements, and petitions;
(d) To appear before courts, tribunals, and authorities;
(e) To compromise, settle, or arbitrate disputes.

4. GOVERNMENT AND STATUTORY MATTERS:
(a) To apply for and obtain No Objection Certificates, clearances, and permissions;
(b) To file tax returns and respond to notices;
(c) To represent before government departments and authorities;
(d) To sign and submit applications and documents.

5. GENERAL POWERS:
(a) To sign, execute, and deliver all documents;
(b) To give undertakings, affidavits, and indemnities;
(c) To appoint and remove sub-agents (with prior written consent);
(d) To do all acts necessary for the above purposes.`,
            },
            {
                id: 'b6',
                type: 'heading',
                content: 'TERMS AND CONDITIONS',
            },
            {
                id: 'b7',
                type: 'clause',
                content: `1. The Attorney shall act in good faith and in the best interest of the Principal at all times.

2. The Attorney shall maintain complete accounts of all transactions and provide statements to the Principal upon request.

3. The Attorney shall not sell, transfer, or encumber any immovable property without specific written authorization for each transaction.

4. The Attorney shall be entitled to reasonable remuneration/reimbursement of expenses as mutually agreed.

5. The Principal hereby ratifies and confirms all acts done by the Attorney within the scope of this Power of Attorney.

6. This Power of Attorney shall remain valid for a period of {{validity_period}} years from the date of execution, unless revoked earlier in writing.

7. This Power of Attorney is irrevocable to the extent permitted by law.`,
            },
            {
                id: 'b8',
                type: 'heading',
                content: 'PROPERTY SCHEDULE (If Applicable)',
            },
            {
                id: 'b9',
                type: 'paragraph',
                content: `{{property_description}}`,
            },
            {
                id: 'b10',
                type: 'signature',
                content: `IN WITNESS WHEREOF, the Principal has executed this Power of Attorney on the date first above written.


________________________
PRINCIPAL/EXECUTANT
{{principal_name}}


ACCEPTED BY ATTORNEY:

I, {{agent_name}}, hereby accept the appointment as Attorney under this Power of Attorney.


________________________
ATTORNEY
{{agent_name}}


WITNESSES:

1. Name: {{witness_1_name}}
   Signature: ___________________
   Address: _____________________

2. Name: {{witness_2_name}}
   Signature: ___________________
   Address: _____________________


Place: _______________________
Date: {{execution_date}}

(To be registered before the Sub-Registrar)`,
            },
        ],
    },
];

export default starterTemplates;
